import { AxiosResponse } from 'axios'
import { IFilter, UpdateBody, Where } from 'types/common'
import { api } from 'API'
import {
  IWorkPlan,
  IWorkPlanWithRelations,
  IWorkStation,
  IWorkPlanDetailWithStations,
  IStationDetail,
  IDuplicatePlanResponse,
  IPracticePeriod,
  IPracticeLane,
  IPracticeSegment,
  IPracticeStation,
  CreatePracticePeriodDto,
  CreatePracticeLaneDto,
  CreatePracticeSegmentDto,
  CreatePracticeStationDto
} from 'interfaces/plan'

export async function getLineBalanceById(
  lineBalanceId: number,
  filter: IFilter<IWorkPlan> = {},
  options?: {
    includeAssignees?: boolean
    practiceEventId?: number
    includeStepContent?: boolean
    /** Org that owns this plan. Overrides the interceptor's localStorage stamp. */
    orgId?: number
  }
): Promise<IWorkPlanDetailWithStations> {
  const params = new URLSearchParams()
  params.append('filter', JSON.stringify(filter))
  if (options?.includeAssignees !== undefined) {
    params.append('includeAssignees', String(options.includeAssignees))
  }
  if (options?.practiceEventId !== undefined) {
    params.append('practiceEventId', String(options.practiceEventId))
  }
  if (options?.includeStepContent !== undefined) {
    params.append('includeStepContent', String(options.includeStepContent))
  }
  // Named explicitly so the read is scoped to the org that owns the record rather
  // than whichever org the interceptor would stamp from localStorage — they differ
  // for anyone who belongs to more than one, and the player surface has none.
  if (options?.orgId !== undefined) {
    params.append('orgId', String(options.orgId))
  }
  const response: AxiosResponse = await api.get(`/line-balances/${lineBalanceId}?${params.toString()}`)
  return response.data
}

export async function getAllLineBalances(
  filter: IFilter<IWorkPlan> = {},
  orgId?: number
): Promise<IWorkPlanWithRelations[]> {
  const response: AxiosResponse = await api.get('/line-balances', {
    params: orgId != null ? { filter: JSON.stringify(filter), orgId } : { filter: JSON.stringify(filter) }
  })
  return response.data
}

export interface ILineBalanceOption {
  _id: number
  name: string
  isVariant: boolean
}

export async function getLineBalanceOptions(params: {
  isArchived?: boolean
}): Promise<{ options: ILineBalanceOption[] }> {
  const response: AxiosResponse = await api.post('/line-balances/options', params)
  return response.data
}

export async function searchLineBalances(
  filterDto: {
    page?: number
    pageSize?: number
    search?: string
    sortBy?: string
    isArchived?: boolean
    createdAt?: string
    creatorIds?: number[]
    /** BE filters line-balances by this body field (or `orgIds`). Must be the
     *  active team's id for per-team scoping; omit to fetch unscoped. */
    organizationId?: number
  },
  orgId?: number
): Promise<{ results: (IWorkPlanWithRelations & { periodCount: number })[]; totalCount: number }> {
  // BE scopes line-balance search by the body `organizationId`/`orgIds`; the
  // `orgId` query is sent in parallel to satisfy the org-context auth guard.
  const response: AxiosResponse = await api.post(
    '/line-balances/search',
    filterDto,
    orgId != null ? { params: { orgId } } : undefined
  )
  return response.data
}

export async function countLineBalances(where: Where<IWorkPlan> = {}): Promise<number> {
  const response: AxiosResponse = await api.get('/line-balances/count', {
    params: { where: JSON.stringify(where) }
  })
  return response?.data?.count ?? 0
}

export async function createLineBalance(
  createDto: {
    name: string
    description?: string
    status?: number
  },
  orgId?: number
): Promise<IWorkPlan & { id: number }> {
  const response: AxiosResponse = await api.post(
    '/line-balances',
    createDto,
    orgId != null ? { params: { orgId } } : undefined
  )
  return response.data
}

export async function updateLineBalanceById(id: number, updateBody: UpdateBody<IWorkPlan>): Promise<void> {
  await api.patch(`/line-balances/${id}`, updateBody)
}

export async function deleteLineBalanceById(id: number): Promise<void> {
  await api.delete(`/line-balances/${id}`)
}

export async function archiveMultipleLineBalances(ids: number[]): Promise<void> {
  await api.patch('/line-balances/archive-multiple', { ids })
}

export async function restoreLineBalanceById(id: number): Promise<void> {
  await api.patch(`/line-balances/${id}/restore`)
}

export async function restoreMultipleLineBalances(ids: number[]): Promise<void> {
  await api.patch('/line-balances/restore-multiple', { ids })
}

export async function createWorkStation(lineBalanceId: number): Promise<IWorkStation> {
  const response: AxiosResponse = await api.post('/work-stations', { lineBalanceId })
  return response.data
}

export async function linkStepsToWorkStation(payload: {
  workStationId: number
  stepIds: number[]
  allStationStepPositions: { id: number; position: number }[]
  newStepIds: number[]
}) {
  const response: AxiosResponse = await api.post('/steps-work-stations', payload)
  return response.data
}

export async function addStepsToEndOfStation(payload: { workStationId: number; stepIds: number[] }): Promise<void> {
  await api.post('/steps-work-stations/add-to-end', payload)
}

export async function addStepsWithPositions(payload: {
  workStationId: number
  stepPositions: Array<{ stepId: number; position: number; isNew?: boolean }>
}): Promise<void> {
  await api.post('/steps-work-stations/add-with-positions', payload)
}

export async function unlinkStepFromWorkStation(workStationId: number, stepId: number): Promise<void> {
  await api.delete(`/steps-work-stations/${workStationId}`, { data: { stepIds: [stepId] } })
}

export async function unlinkStepsFromWorkStation(workStationId: number, stepIds: number[]): Promise<void> {
  await api.delete(`/steps-work-stations/${workStationId}`, { data: { stepIds } })
}

export async function deleteWorkStation(workStationId: number): Promise<void> {
  await api.delete(`/work-stations/${workStationId}`)
}

export async function updateWorkStationById(
  id: number,
  updateBody: Partial<Pick<IWorkStation, 'name' | 'duration'>>
): Promise<void> {
  await api.patch(`/work-stations/${id}`, updateBody)
}

export async function updateStepsPositionsInStation(
  workStationId: number,
  stepPositions: { id: number; position: number }[]
): Promise<void> {
  await api.patch('/steps-work-stations/positions/batch', {
    workStationId,
    stepPositions
  })
}

export async function moveStepBetweenStations(payload: {
  stepIds: number[]
  fromStationId: number
  toStationId: number
  newStationStepPositions: { id: number; position: number }[]
}): Promise<void> {
  await api.patch('/steps-work-stations/move-step', payload)
}

export async function getStationDetailById(stationId: number): Promise<IStationDetail> {
  const response: AxiosResponse = await api.get(`/work-stations/${stationId}`)
  return response.data
}

export async function createWorkplanVariant(originId: number, name?: string): Promise<IDuplicatePlanResponse> {
  const response: AxiosResponse = await api.post(`/line-balances/${originId}/variants`, { name })
  return response.data
}

export async function duplicateWorkplan(sourceId: number, name?: string): Promise<IDuplicatePlanResponse> {
  const response: AxiosResponse = await api.post(`/line-balances/${sourceId}/duplicate`, { name })
  return response.data
}

export interface ReviewSettings {
  requireChangesReview: boolean
  reviewOption?: number
}

export async function updateReviewSettings(workplanId: number, settings: ReviewSettings): Promise<void> {
  await api.patch(`/line-balances/${workplanId}/review-settings`, settings)
}

// Edit lock APIs
export async function lockLineBalance(
  lineBalanceId: number
): Promise<{ editLockOwnerId: number; editLockExpirationAt: string }> {
  const response: AxiosResponse = await api.post(`/line-balances/${lineBalanceId}/lock`)
  return response.data
}

export async function extendLockLineBalance(lineBalanceId: number): Promise<{ editLockExpirationAt: string }> {
  const response: AxiosResponse = await api.post(`/line-balances/${lineBalanceId}/extend-lock`)
  return response.data
}

export async function releaseLockLineBalance(lineBalanceId: number): Promise<void> {
  await api.post(`/line-balances/${lineBalanceId}/release-lock`)
}

export async function forceLockLineBalance(
  lineBalanceId: number
): Promise<{ editLockOwnerId: number; editLockExpirationAt: string }> {
  const response: AxiosResponse = await api.post(`/line-balances/${lineBalanceId}/force-lock`)
  return response.data
}

export async function createPracticePeriod(data: CreatePracticePeriodDto): Promise<IPracticePeriod> {
  const response: AxiosResponse = await api.post('/practice-periods', data)
  return response.data
}

export async function updatePracticePeriod(id: number, data: Partial<IPracticePeriod>): Promise<void> {
  await api.patch(`/practice-periods/${id}`, data)
}

export async function deletePracticePeriod(id: number): Promise<void> {
  await api.delete(`/practice-periods/${id}`)
}

export async function getPracticePeriodsByLineBalance(lineBalanceId: number): Promise<IPracticePeriod[]> {
  const response: AxiosResponse = await api.get(`/practice-periods/by-line-balance/${lineBalanceId}`)
  return response.data
}

export async function createPracticeLane(data: CreatePracticeLaneDto): Promise<IPracticeLane> {
  const response: AxiosResponse = await api.post('/practice-lanes', data)
  return response.data
}

export async function updatePracticeLane(id: number, data: Partial<IPracticeLane>): Promise<void> {
  await api.patch(`/practice-lanes/${id}`, data)
}

export async function deletePracticeLane(id: number): Promise<void> {
  await api.delete(`/practice-lanes/${id}`)
}

export async function createPracticeSegment(data: CreatePracticeSegmentDto): Promise<IPracticeSegment> {
  const response: AxiosResponse = await api.post('/practice-segments', data)
  return response.data
}

export async function updatePracticeSegment(id: number, data: Partial<IPracticeSegment>): Promise<void> {
  await api.patch(`/practice-segments/${id}`, data)
}

export async function updateSegmentPositions(
  updates: { id: number; position: number; practiceLaneId?: number }[]
): Promise<void> {
  // Use batch endpoint for better performance
  await api.patch('/practice-segments/batch', { updates })
}

export async function deletePracticeSegment(id: number): Promise<void> {
  await api.delete(`/practice-segments/${id}`)
}

export async function createPracticeStation(data: CreatePracticeStationDto): Promise<IPracticeStation> {
  const response: AxiosResponse = await api.post('/work-stations', data)
  return response.data
}

export async function updatePracticeStation(id: number, data: Partial<IPracticeStation>): Promise<void> {
  await api.patch(`/work-stations/${id}`, data)
}

export async function deletePracticeStation(id: number): Promise<void> {
  await api.delete(`/work-stations/${id}`)
}

export async function createFirstStationInPeriod(
  periodId: number,
  data?: { laneName?: string; segmentName?: string; stationName?: string }
): Promise<{ lane: IPracticeLane; segment: IPracticeSegment; station: IPracticeStation }> {
  const response: AxiosResponse = await api.post(`/practice-periods/${periodId}/first-station`, data || {})
  return response.data
}

export async function createFirstSegmentInPeriod(
  periodId: number,
  data?: { laneName?: string; segmentName?: string }
): Promise<{ lane: IPracticeLane; segment: IPracticeSegment }> {
  const response: AxiosResponse = await api.post(`/practice-periods/${periodId}/first-segment`, data || {})
  return response.data
}

export async function duplicatePracticeSegment(
  segmentId: number,
  targetLaneId: number,
  position: 'beginning' | 'end'
): Promise<IPracticeSegment> {
  const response: AxiosResponse = await api.post(`/practice-segments/${segmentId}/duplicate`, {
    targetLaneId,
    position
  })
  return response.data
}

export async function duplicatePracticeStation(
  stationId: number,
  targetSegmentId: number,
  position: 'beginning' | 'end'
): Promise<IPracticeStation> {
  const response: AxiosResponse = await api.post(`/work-stations/${stationId}/duplicate`, {
    targetSegmentId,
    position
  })
  return response.data
}

export async function duplicatePracticeLane(
  laneId: number,
  targetPeriodId: number,
  position: 'beginning' | 'end'
): Promise<IPracticeLane> {
  const response: AxiosResponse = await api.post(`/practice-lanes/${laneId}/duplicate`, {
    targetPeriodId,
    position
  })
  return response.data
}

export async function duplicatePracticePeriod(
  periodId: number,
  lineBalanceId: number,
  position: 'beginning' | 'end'
): Promise<IPracticePeriod> {
  const response: AxiosResponse = await api.post(`/practice-periods/${periodId}/duplicate`, {
    lineBalanceId,
    position
  })
  return response.data
}
